import { countries, CountryIsoCode } from "@goodfill/domain-customer-application-types";
import { Flex, InputSelect, InputSelectOption } from "@goodfill/ui-components";
import { FieldGroup } from "../form/FieldGroup";
import { countryCodeEmoji } from "country-code-emoji";
import { registerLocale, alpha3ToAlpha2 } from "i18n-iso-countries";
import enLanguageLocale from "i18n-iso-countries/langs/en.json";
import { FC, forwardRef, Ref } from "react";

registerLocale(enLanguageLocale);

const fixCountryCodeTypos = (code: string) => (code === "LUZ" ? "LUX" : code === "DBK" ? "DNK" : code);

export const countriesOptions: InputSelectOption[] = Object.entries(countries)
  .map(([key, value]) => ({
    value: key,
    label: `${countryCodeEmoji(alpha3ToAlpha2(fixCountryCodeTypos(key)))} ${value}`,
  }))
  .sort(
    // Most users are from the United States of America, show it as first option.
    ({ value }) => (value === "USA" || value === "CAN" ? -1 : 1),
  );

type CountryProps = {
  onChange: (value: CountryIsoCode | null) => void;
  selected: CountryIsoCode | null;
  validation: { message?: string } | undefined;
  title?: string;
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
  ref?: Ref<HTMLSelectElement>;
  id?: string;
  disabled?: boolean;
};

type CountryInputSelectProps = Omit<CountryProps, "title">;

const CountryInputSelect: FC<CountryInputSelectProps> = forwardRef(
  ({ selected, validation, onChange, onBlur, id, disabled }, ref) => (
    <InputSelect
      placeholder="Select country"
      options={countriesOptions}
      selected={selected ?? ""}
      border={validation === undefined ? "neutral" : "red"}
      onChange={(value: string) => onChange(value as CountryIsoCode)}
      onBlur={onBlur}
      ref={ref}
      id={id}
      disabled={disabled}
    />
  ),
);

export const CountryInput: React.FC<CountryProps> = forwardRef(
  ({ title, validation, selected, onChange, onBlur, id, disabled }, ref) => {
    const selectComponent = (
      <CountryInputSelect
        selected={selected}
        validation={validation}
        onChange={onChange}
        onBlur={onBlur}
        ref={ref}
        id={id}
        disabled={disabled}
      />
    );
    return (
      <Flex>
        {title ? (
          <FieldGroup
            label={title}
            validation={validation?.message ? [validation?.message] : []}
            className="w-80-sm w-full-after-md"
            fill
          >
            {selectComponent}
          </FieldGroup>
        ) : (
          selectComponent
        )}
      </Flex>
    );
  },
);
